import { useEffect, useMemo, useState } from 'react';
import axiosConn from '../../axiosConn';

const PROOF_CARDS = [
  {
    key: 'radioRotation',
    kicker: 'ROTATION',
    title: 'Radio Rotation is live',
    activeBody:
      'Rights-clean records are already active inside Joe Galaxy Radio Rotation.',
    fallbackBody:
      'Rights-clean records can move into Joe Galaxy Radio Rotation after review.',
    countLabel: 'records in rotation',
  },
  {
    key: 'listening',
    kicker: 'LISTENING',
    title: 'Listens have to count',
    activeBody:
      'Radio plays count only after someone listens long enough - at least 30 seconds or 25% of the record.',
    fallbackBody:
      'JG Radio tracks listening activity through public-safe aggregate play signals.',
    countLabel: 'counted listens',
  },
  {
    key: 'feedback',
    kicker: 'FEEDBACK',
    title: 'Feedback is structured',
    activeBody:
      'Listeners can leave signal after they spend time with a record. Praise is not required. Honest reaction is the point.',
    fallbackBody:
      'Listener reaction is collected through structured signal, not loose public comments.',
    countLabel: 'feedback signals submitted',
  },
  {
    key: 'support',
    kicker: 'SUPPORT',
    title: 'Support is wired in',
    activeBody:
      'Eligible records can connect to JGK artist support inside Radio. When support happens, it becomes signal.',
    fallbackBody:
      'Eligible records can connect to JGK artist support inside Radio when the support path is ready.',
    countLabel: 'support actions',
  },
];

const getProofData = (response) => response?.data?.data || null;

const getDisplayCount = (value) => {
  const count = Number(value);
  if (!Number.isFinite(count) || count <= 0) return null;
  return Math.round(count);
};

const cleanDisplayText = (value) => String(value || '').trim();

const getSupportAmountProof = (proof) => {
  const support = proof?.support || {};
  if (!support.displaySupportAmount) return null;
  const jgk = cleanDisplayText(support.supportJgkDisplay);
  const usd = cleanDisplayText(support.supportUsdDisplay);
  return jgk && usd ? { jgk, usd } : null;
};

const hasCardSignal = (cardKey, proof) => {
  if (cardKey === 'radioRotation') {
    return Boolean(proof?.radioRotation?.hasActiveRotation);
  }
  if (cardKey === 'listening') {
    return Boolean(proof?.listening?.hasCountedPlays);
  }
  if (cardKey === 'feedback') {
    return Boolean(proof?.feedback?.hasFeedbackSignal);
  }
  if (cardKey === 'support') {
    return Boolean(proof?.support?.hasSupportReady || proof?.support?.hasSupportActions);
  }
  return false;
};

const hasProofActivity = (proof) =>
  PROOF_CARDS.some((card) => hasCardSignal(card.key, proof));

const resolveCardTitle = ({ card, proof }) => {
  if (card.key !== 'support') return card.title;
  if (getSupportAmountProof(proof)) return 'JGK support is happening';
  if (proof?.support?.hasSupportActions) return 'Support signals have started';
  return card.title;
};

const resolveCardBody = ({ card, proof }) => {
  if (card.key === 'support') {
    if (getSupportAmountProof(proof)) {
      return 'Records that connect can earn more than comments. JGK artist support is already becoming part of the signal inside Radio.';
    }
    if (proof?.support?.displayCount) {
      return 'When a record connects, listeners can support the artist inside JG Radio.';
    }
    if (proof?.support?.hasSupportActions) return card.activeBody;
    return card.fallbackBody;
  }
  if (!hasCardSignal(card.key, proof)) return card.fallbackBody;
  if (card.key !== 'listening') return card.activeBody;
  return proof?.listening?.thresholdQualified === true
    ? card.activeBody
    : card.fallbackBody;
};

const resolveCardCount = ({ card, proof, displayCount }) => {
  if (card.key === 'support') {
    const supportAmount = getSupportAmountProof(proof);
    if (supportAmount) {
      return `${supportAmount.jgk} support signal (${supportAmount.usd})`;
    }
  }
  if (!displayCount) return '';
  return `${displayCount}+ ${card.countLabel}`;
};

export default function HitLabProofSignalSection({
  enabled,
  onStartIntake,
  ctaLabel = 'Put My Record In The Lab',
}) {
  const [proof, setProof] = useState(null);

  useEffect(() => {
    let active = true;

    const loadProof = async () => {
      try {
        const response = await axiosConn.get('hitlab/public-proof');
        if (active) setProof(getProofData(response));
      } catch {
        if (active) setProof(null);
      }
    };

    void loadProof();
    return () => {
      active = false;
    };
  }, []);

  const activeCopy = hasProofActivity(proof);
  const countByCard = useMemo(
    () => ({
      radioRotation: getDisplayCount(proof?.radioRotation?.displayCount),
      listening: getDisplayCount(proof?.listening?.displayCount),
      feedback: getDisplayCount(proof?.feedback?.displayCount),
      support: getDisplayCount(proof?.support?.displayCount),
    }),
    [proof],
  );

  return (
    <section
      className="hitlab-shell hitlab-proof-signal-section"
      aria-label="JG HitLab public proof signals"
    >
      <div className="hitlab-proof-signal-header">
        <p className="hitlab-kicker">REAL HITLAB SIGNAL</p>
        <h2>{activeCopy ? 'The room is already moving.' : 'The room is built for proof.'}</h2>
        <p>
          {activeCopy
            ? 'JG HitLab is not a dead upload box. Records are already moving through Joe Galaxy Radio Rotation, real listening thresholds, structured feedback, and support paths inside JG Universe.'
            : 'JG HitLab is not a dead upload box. Records enter a pipeline built for Radio Rotation, public-safe play signals, structured feedback, and support inside JG Universe.'}
        </p>
      </div>
      <div className="hitlab-proof-signal-grid">
        {PROOF_CARDS.map((card) => {
          const displayCount = countByCard[card.key];
          const displayText = resolveCardCount({ card, proof, displayCount });
          return (
            <article key={card.key} className="hitlab-proof-signal-card">
              <span>{card.kicker}</span>
              <h3>{resolveCardTitle({ card, proof })}</h3>
              <p>{resolveCardBody({ card, proof })}</p>
              {displayText ? (
                <strong className="hitlab-proof-signal-count">
                  {displayText}
                </strong>
              ) : null}
            </article>
          );
        })}
      </div>
      <div className="hitlab-proof-signal-close">
        <p>Placement cannot be bought. The record has to prove it.</p>
        <button
          type="button"
          className="hitlab-primary-btn"
          onClick={onStartIntake}
          disabled={!enabled}
        >
          {ctaLabel}
        </button>
      </div>
    </section>
  );
}
